import { API_BASE, messageFromErrorBody } from './authApi';
import { authHeaders } from './authStorage';

const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  ...authHeaders(),
});

const clean = (v) => (typeof v === 'string' ? v.trim() : '');

export function mapMeetingFromApi(d) {
  return {
    id: Number(d.id || 0),
    companyId: Number(d.companyId || 0),
    companyName: d.companyName ?? '',
    committeeId: d.committeeId ? String(d.committeeId) : '',
    committeeName: d.committeeName ?? '',
    boardId: d.boardId ? String(d.boardId) : '',
    boardName: d.boardName ?? '',
    title: d.title ?? '',
    goal: d.goal ?? '',
    status: d.status ?? 'draft',
    attendanceType: d.attendanceType ?? 'in_person',
    location: d.location ?? '',
    agenda: d.agenda ?? '',
    zoomLink: d.zoomLink ?? '',
    startAtUtc: d.startAtUtc ?? '',
    endAtUtc: d.endAtUtc ?? '',
    scheduledAtUtc: d.scheduledAtUtc ?? '',
    durationMinutes: d.durationMinutes ?? '',
    notes: d.notes ?? '',
    invitees: Array.isArray(d.invitees) ? d.invitees.map((x) => ({
      targetType: x.targetType ?? '',
      targetId: String(x.targetId ?? ''),
      displayName: x.displayName ?? '',
    })) : [],
    createdByUserId: d.createdByUserId ?? '',
    createdAtUtc: d.createdAtUtc ?? '',
    updatedAtUtc: d.updatedAtUtc ?? '',
    isOpen: !!d.isOpen,
  };
}

export function mapMeetingFormToPayload(form) {
  return {
    companyId: Number(form.companyId),
    committeeId: form.committeeId ? Number(form.committeeId) : null,
    boardId: form.boardId ? Number(form.boardId) : null,
    title: clean(form.title),
    goal: clean(form.goal),
    status: clean(form.status || 'draft'),
    attendanceType: clean(form.attendanceType || 'in_person'),
    location: clean(form.location),
    startAtUtc: form.startAtUtc,
    endAtUtc: form.endAtUtc,
    agenda: clean(form.agenda) || null,
    zoomLink: clean(form.zoomLink) || null,
    durationMinutes: form.durationMinutes === '' || form.durationMinutes == null ? null : Number(form.durationMinutes),
    notes: clean(form.notes) || null,
    invitees: (Array.isArray(form.invitees) ? form.invitees : []).map((x) => ({
      targetType: clean(x.targetType),
      targetId: clean(String(x.targetId)),
      displayName: clean(x.displayName),
    })),
    isOpen: !!form.isOpen,
  };
}

export async function fetchMeetings() {
  const res = await fetch(`${API_BASE}/api/meetings`, { headers: { Accept: 'application/json', ...authHeaders() } });
  const data = await res.json().catch(() => ([]));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر تحميل الاجتماعات'));
  return (Array.isArray(data) ? data : []).map(mapMeetingFromApi);
}

export async function fetchMeetingById(id) {
  const res = await fetch(`${API_BASE}/api/meetings/${id}`, { headers: { Accept: 'application/json', ...authHeaders() } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر تحميل الاجتماع'));
  return mapMeetingFromApi(data);
}

export async function createMeeting(payload) {
  const res = await fetch(`${API_BASE}/api/meetings`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر إنشاء الاجتماع'));
  return mapMeetingFromApi(data);
}

export async function updateMeeting(id, payload) {
  const res = await fetch(`${API_BASE}/api/meetings/${id}`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر تحديث الاجتماع'));
  return mapMeetingFromApi(data);
}

export async function deleteMeeting(id) {
  const res = await fetch(`${API_BASE}/api/meetings/${id}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (res.status === 204) return;
  const data = await res.json().catch(() => ({}));
  throw new Error(messageFromErrorBody(data, 'تعذر حذف الاجتماع'));
}

export async function fetchMeetingParticipantOptions(companyId) {
  const res = await fetch(`${API_BASE}/api/meetings/participant-options?companyId=${encodeURIComponent(companyId)}`, {
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  const data = await res.json().catch(() => ([]));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر تحميل المشاركين'));
  return Array.isArray(data) ? data.map((x) => ({
    groupId: x.groupId ?? '',
    groupLabel: x.groupLabel ?? '',
    targetType: x.targetType ?? '',
    targetId: String(x.targetId ?? ''),
    displayName: x.displayName ?? '',
    subtitle: x.subtitle ?? '',
  })) : [];
}

export async function fetchMeetingWorkspace(id) {
  const res = await fetch(`${API_BASE}/api/meetings/${id}/workspace`, {
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر تحميل هيكل الاجتماع'));
  return {
    agendaItems: Array.isArray(data.agendaItems) ? data.agendaItems : [],
    tasks: Array.isArray(data.tasks) ? data.tasks : [],
    attendance: Array.isArray(data.attendance) ? data.attendance : [],
    attachments: Array.isArray(data.attachments) ? data.attachments : [],
    minutes: data.minutes || { summary: '', decisions: '' },
  };
}

async function saveTab(id, tab, payload, fallbackMessage) {
  const res = await fetch(`${API_BASE}/api/meetings/${id}/${tab}`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  if (res.status === 204) return;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, fallbackMessage));
}

export const saveMeetingAgenda = (id, items) => saveTab(id, 'agenda', { items }, 'تعذر حفظ جدول الأعمال');
export const saveMeetingTasks = (id, items) => saveTab(id, 'tasks', { items }, 'تعذر حفظ المهام');
export const saveMeetingAttendance = (id, items) => saveTab(id, 'attendance', { items }, 'تعذر حفظ الحضور');
export const saveMeetingAttachments = (id, items) => saveTab(id, 'attachments', { items }, 'تعذر حفظ المرفقات');
export const saveMeetingMinutes = (id, minutes) => saveTab(id, 'minutes', minutes, 'تعذر حفظ المحضر');

export async function resendMeetingInvitation(id) {
  console.group(`📧 [Meeting ${id}] Resend Invitation`);
  console.log('🕐 Sending request at', new Date().toISOString());
  const res = await fetch(`${API_BASE}/api/meetings/${id}/resend-invitation`, {
    method: 'POST',
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    console.error('❌ Request failed', { status: res.status, body: data });
    console.groupEnd();
    throw new Error(messageFromErrorBody(data, 'تعذر إعادة إرسال الدعوة'));
  }
  console.log('✅ Response received:', data);
  console.log('📬 Emails sent to:', data.emailsSent);
  console.table(
    (data.emailsSent || []).map((email, i) => ({ '#': i + 1, email }))
  );
  console.log(`📊 Total sent: ${data.sentCount}`);
  console.log('🕐 Sent at (UTC):', data.sentAtUtc);
  console.groupEnd();
  return data;
}
